import { useCallback, useEffect, useState } from 'react'
import { fetchPipeline, secondSpark, moveStage, currentUserName, type Pipeline, type PipelineCard } from '../../lib/api'
import BriefComposer from './BriefComposer'

// The Incubator pipeline board.
// Built 2026-07-12 (Ember, task-006) against the /pipeline endpoint from task-007.
//
// This board is Joel's inbox first and a Kanban second. The Incubator ran 70 days
// with 21 Sparks and zero Briefs — not from apathy (every Spark has discussion)
// but because the promotion mechanism did not exist. So the empty columns here
// explain what would fill them. An empty column that says nothing reads as a dead
// project; an empty column that names its own gate reads as an instruction.

const BIZ_DEV = ['vector', 'cipher', 'ledger', 'counsel']

const STAGES = ['Spark', 'Brief', 'In Review', 'Research'] as const
const STAGE_COLOR: Record<string, string> = {
  Spark: '#5AC884',
  Brief: '#6FB8D6',
  'In Review': '#E6C85A',
  Research: '#B265D6',
  Build: '#5AC884',
  Parked: '#8A8A94',
  'No-Go': '#D65A5A',
}
const STAGE_EMOJI: Record<string, string> = {
  Spark: '💡', Brief: '🔍', 'In Review': '📋', Research: '🔬',
  Build: '✅', Parked: '🅿️', 'No-Go': '❌',
}

// Why is this column empty? Each answer names the gate that would fill it.
const EMPTY_REASON: Record<string, string> = {
  Spark: 'No Sparks yet. Any persona can post one.',
  Brief: 'No Briefs yet. A Spark needs one second from another persona, then Biz Dev or the author writes the Brief.',
  'In Review': 'Nothing waiting on Joel. A Brief lands here when its author submits it.',
  Research: 'No research running. Joel greenlights a Brief in Review to start a time-boxed sprint.',
}

export default function IncubatorBoard({ slug = 'the-incubator' }: { slug?: string }) {
  const [pipeline, setPipeline] = useState<Pipeline | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [flash, setFlash] = useState<{ id: string; msg: string; bad: boolean } | null>(null)
  const [showClosed, setShowClosed] = useState(false)
  const [briefing, setBriefing] = useState<PipelineCard | null>(null)

  const me = currentUserName()
  const isJoel = (me || '').toLowerCase() === 'joel'

  const load = useCallback(() => {
    fetchPipeline(slug)
      .then(setPipeline)
      .catch(e => setError(e.message || 'Failed to load pipeline'))
  }, [slug])

  useEffect(load, [load])

  // Server enforces every gate. We call it and surface what it says — the 403/409
  // messages explain the rule better than a disabled button ever could.
  async function act(card: PipelineCard, fn: () => Promise<unknown>) {
    setBusy(card.id)
    setFlash(null)
    try {
      await fn()
      const next = await fetchPipeline(slug)
      setPipeline(next)
    } catch (e) {
      let msg = e instanceof Error ? e.message : 'failed'
      try { msg = JSON.parse(msg).error || msg } catch { /* plain text */ }
      setFlash({ id: card.id, msg, bad: true })
    }
    setBusy(null)
  }

  if (error) return <Notice text={`Pipeline unavailable — ${error}`} bad />
  if (!pipeline) return <Notice text="Loading pipeline…" />

  const waiting = pipeline.awaiting_joel.length
  const ready = pipeline.ready_for_brief.length
  const closed = [...pipeline.parking_lot, ...pipeline.graveyard, ...(pipeline.columns['Build'] || [])]

  return (
    <div style={{ marginBottom: 32 }}>
      {/* Joel's inbox. The single thing the board exists to answer. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          padding: '14px 18px', marginBottom: 18, borderRadius: 12,
          background: waiting ? 'rgba(230,200,90,0.10)' : 'var(--bg-elevated, rgba(255,255,255,0.03))',
          border: `1px solid ${waiting ? 'rgba(230,200,90,0.45)' : 'var(--border-subtle, rgba(255,255,255,0.08))'}`,
        }}
      >
        <span style={{ fontSize: 20 }}>{waiting ? '📋' : '🫗'}</span>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {waiting
              ? `${waiting} ${waiting === 1 ? 'idea is' : 'ideas are'} waiting on Joel`
              : 'Nothing is waiting on Joel'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            {waiting
              ? 'Green advances it. Yellow sends it back with notes. Red retires it to the Graveyard.'
              : ready
                ? `${ready} ${ready === 1 ? 'Spark has' : 'Sparks have'} earned a second and can be written up as a Brief.`
                : 'No Spark has been seconded yet. A Spark needs one second from another persona to move.'}
          </div>
        </div>
      </div>

      {/* The pipeline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, alignItems: 'start' }}>
        {STAGES.map(stage => {
          const cards = pipeline.columns[stage] || []
          const color = STAGE_COLOR[stage]
          return (
            <div key={stage} style={{ background: 'var(--bg-elevated, rgba(255,255,255,0.02))', border: '1px solid var(--border-subtle, rgba(255,255,255,0.07))', borderRadius: 12, padding: 12, minHeight: 130 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, paddingBottom: 9, borderBottom: `2px solid ${color}` }}>
                <span>{STAGE_EMOJI[stage]}</span>
                <span style={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase' }}>{stage}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color, background: `${color}22`, padding: '2px 8px', borderRadius: 20 }}>{cards.length}</span>
              </div>

              {cards.length === 0 ? (
                <p style={{ fontSize: 11, lineHeight: 1.55, color: 'var(--text-muted)', margin: '10px 2px', fontStyle: 'italic' }}>
                  {EMPTY_REASON[stage]}
                </p>
              ) : (
                cards.map(card => (
                  <Card
                    key={card.id}
                    card={card}
                    me={me}
                    isJoel={isJoel}
                    busy={busy === card.id}
                    flash={flash?.id === card.id ? flash : null}
                    onSecond={() => act(card, () => secondSpark(card.id))}
                    onMove={(to, note) => act(card, () => moveStage(card.id, to, note))}
                    onWriteBrief={() => setBriefing(card)}
                  />
                ))
              )}
            </div>
          )
        })}
      </div>

      {/* Graveyard + Parking Lot — killed ideas are contributions, not failures. */}
      <button
        onClick={() => setShowClosed(v => !v)}
        style={{ marginTop: 16, padding: '7px 13px', fontSize: 11, borderRadius: 8, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.1))', cursor: 'pointer', fontFamily: 'inherit' }}
      >
        {showClosed ? '▾' : '▸'} Decided — {pipeline.counts['Build'] || 0} built · {pipeline.counts['Parked'] || 0} parked · {pipeline.counts['No-Go'] || 0} in the Graveyard
      </button>

      {showClosed && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
          {closed.length === 0 ? (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
              Nothing has reached a decision yet. The Graveyard is where the community learns what doesn't work — it fills up only once ideas start reaching Joel.
            </p>
          ) : closed.map(card => (
            <Card key={card.id} card={card} me={me} isJoel={isJoel} busy={false} flash={null} onSecond={() => {}} onMove={() => {}} onWriteBrief={() => {}} />
          ))}
        </div>
      )}

      {briefing && (
        <BriefComposer
          postId={briefing.id}
          title={briefing.title}
          onClose={() => setBriefing(null)}
          onSaved={() => { setBriefing(null); load() }}
        />
      )}
    </div>
  )
}

function Card({ card, me, isJoel, busy, flash, onSecond, onMove, onWriteBrief }: {
  card: PipelineCard
  me: string | null
  isJoel: boolean
  busy: boolean
  flash: { msg: string; bad: boolean } | null
  onSecond: () => void
  onMove: (to: string, note?: string) => void
  onWriteBrief: () => void
}) {
  const color = STAGE_COLOR[card.stage] || '#8A7FB8'
  const mine = me === card.author
  const iSeconded = !!me && card.seconded_by.includes(me)
  const canWriteBrief = !!me && (isJoel || BIZ_DEV.includes(me) || mine)

  return (
    <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle, rgba(255,255,255,0.09))', borderLeft: `3px solid ${color}`, borderRadius: 9, padding: '10px 11px', marginBottom: 9 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, marginBottom: 6 }}>
        {card.title.replace(/^(Spark|Pre-Brief|Brief):\s*/i, '')}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 8 }}>
        <span>{card.author}</span>
        {card.category && <span>· {card.category}</span>}
        {card.replies > 0 && <span>· {card.replies} {card.replies === 1 ? 'reply' : 'replies'}</span>}
      </div>

      {/* Seconds are the currency of the first gate — show them, and who. */}
      <div style={{ fontSize: 10.5, marginBottom: 8, color: card.seconds > 0 ? STAGE_COLOR['Brief'] : 'var(--text-muted)' }}>
        {card.seconds > 0
          ? `${card.seconds} second${card.seconds === 1 ? '' : 's'} — ${card.seconded_by.join(', ')}`
          : 'no seconds yet'}
        {card.ready_for_brief && !card.brief && <strong style={{ color: STAGE_COLOR['Brief'] }}> · ready for a Brief</strong>}
      </div>

      {/* The gate score rides on the card face. A failing gate is never a footnote —
          the whole reason the Incubator stalled is that one got summarized away. */}
      {card.brief && (
        <div style={{ fontSize: 10.5, lineHeight: 1.5, marginBottom: 8, padding: '6px 8px', borderRadius: 6, background: card.brief.failed.length ? 'rgba(214,90,90,0.10)' : 'rgba(90,200,132,0.08)', border: `1px solid ${card.brief.failed.length ? 'rgba(214,90,90,0.35)' : 'rgba(90,200,132,0.28)'}` }}>
          <strong>{card.brief.passed}/5 gates pass</strong>
          {card.brief.failed.length > 0 && <div style={{ color: '#E68A8A' }}>failing: {card.brief.failed.join(', ')}</div>}
          {card.brief.unproven.length > 0 && <div style={{ color: '#E6C85A' }}>unproven: {card.brief.unproven.join(', ')}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {card.stage === 'Spark' && !mine && me && (
          <Btn onClick={onSecond} busy={busy} color={iSeconded ? '#8A8A94' : STAGE_COLOR['Spark']}>
            {iSeconded ? 'Seconded ✓' : 'Second this'}
          </Btn>
        )}
        {card.stage === 'Spark' && mine && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>your Spark — someone else must second it</span>
        )}
        {card.stage === 'Spark' && card.ready_for_brief && canWriteBrief && (
          <Btn onClick={onWriteBrief} busy={busy} color={STAGE_COLOR['Brief']}>
            {card.brief ? 'Revise the Brief' : 'Write the Brief'}
          </Btn>
        )}
        {card.stage === 'Spark' && card.brief_complete && canWriteBrief && (
          <Btn onClick={() => onMove('Brief')} busy={busy} color={STAGE_COLOR['Brief']}>Promote to Brief →</Btn>
        )}
        {card.stage === 'Brief' && canWriteBrief && (
          <Btn onClick={onWriteBrief} busy={busy} color={STAGE_COLOR['Brief']}>Revise</Btn>
        )}
        {card.stage === 'Brief' && (mine || isJoel) && (
          <Btn onClick={() => onMove('In Review')} busy={busy} color={STAGE_COLOR['In Review']}>Submit to Joel →</Btn>
        )}
        {card.stage === 'In Review' && isJoel && (
          <>
            <Btn onClick={() => onMove('Research')} busy={busy} color={STAGE_COLOR['Research']}>Green — research it</Btn>
            <Btn onClick={() => onMove('Brief')} busy={busy} color={STAGE_COLOR['In Review']}>Yellow — revise</Btn>
            <Btn onClick={() => onMove('No-Go')} busy={busy} color={STAGE_COLOR['No-Go']}>Red — kill</Btn>
          </>
        )}
        {card.stage === 'Research' && isJoel && (
          <>
            <Btn onClick={() => onMove('Build')} busy={busy} color={STAGE_COLOR['Build']}>Build it</Btn>
            <Btn onClick={() => onMove('Parked')} busy={busy} color={STAGE_COLOR['Parked']}>Park it</Btn>
            <Btn onClick={() => onMove('No-Go')} busy={busy} color={STAGE_COLOR['No-Go']}>Kill it</Btn>
          </>
        )}
      </div>

      {flash && (
        <div style={{ marginTop: 8, fontSize: 10.5, lineHeight: 1.45, color: flash.bad ? '#E68A8A' : '#8AD6A0' }}>
          {flash.msg}
        </div>
      )}
    </div>
  )
}

function Btn({ onClick, busy, color, children }: { onClick: () => void; busy: boolean; color: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      style={{
        padding: '4px 10px', fontSize: 10.5, fontWeight: 600, borderRadius: 6,
        background: `${color}1F`, color, border: `1px solid ${color}66`,
        cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.5 : 1,
      }}
    >
      {busy ? '…' : children}
    </button>
  )
}

function Notice({ text, bad }: { text: string; bad?: boolean }) {
  return (
    <p style={{ fontSize: 12, color: bad ? '#E68A8A' : 'var(--text-muted)', margin: '18px 2px' }}>{text}</p>
  )
}
